import { cn } from '@sohanemon/utils';
import { Iconify } from '@sohanemon/utils/components';
import { m } from '@sohanscript/i18n';
import { formatDate, parseTags } from '@sohanscript/lib/utils/format';
import { Card } from '@sohanscript/ui/card';
import { Chip } from '@sohanscript/ui/chip';
import {
	HoverCard,
	HoverCardContent,
	HoverCardTrigger,
} from '@sohanscript/ui/hover-card';
import { Motion } from '@sohanscript/ui/motion';
import type { ComponentProps } from 'react';
import { Link } from '@/components/link';
import type { RouterOutputs } from '@/server/root';
import { Img } from '../image';

export type NoteCardProps = ComponentProps<typeof Motion> &
	RouterOutputs['note']['list'][number] & { page?: boolean };

type NoteCardContentProps = Omit<NoteCardProps, 'page'> & {
	date: string | null;
	excerpt: string | null;
};

const DateTree = ({
	date,
	className,
}: {
	date: string;
	className?: string;
}) => (
	<div
		className={cn(
			'relative flex-none ps-3.5 text-secondary text-sm',
			className,
		)}
	>
		<span
			aria-hidden="true"
			className="absolute inset-s-0 inset-y-0 flex items-center"
		>
			<span className="h-4 w-0.5 rounded-full bg-secondary transition-colors group-hover:bg-primary" />
		</span>
		{date}
	</div>
);

/** Derive estimated reading time from body text length (~200 wpm). */
function estimateReadTime(body?: string): string {
	if (!body) return '1 min read';
	const minutes = Math.max(1, Math.ceil(body.length / 1000));
	return `${minutes} min read`;
}

/** Stable short ID from a slug string. */
function noteIdFromSlug(slug: string): string {
	let hash = 0;
	for (let i = 0; i < slug.length; i++) {
		hash = (hash * 31 + slug.charCodeAt(i)) | 0;
	}
	return `NOTE-${Math.abs(hash) % 10000}`;
}

function NotePageCard({
	title,
	date,
	excerpt,
	image,
	slug,
	body: _body,
	description: _description,
	publishedAt: _publishedAt,
	tags: _tags,
	className,
	...props
}: NoteCardContentProps) {
	const href = '/note/$slug' as const;
	const params = { slug: slug?.current ?? '' } as never;
	return (
		<HoverCard>
			<HoverCardTrigger
				render={(triggerProps) => (
					<Link
						to={href}
						params={params}
						viewTransition={{ types: ['slide-left'] }}
						aria-label={m.note_read_card_aria({ title: title ?? '' })}
						{...triggerProps}
					>
						<Motion
							{...props}
							className={cn(
								'group relative md:grid md:grid-cols-4 md:items-baseline',
								className,
							)}
						>
							<article className="corner-bevel relative z-10 -mx-3 rounded-lg px-3 py-3 transition-colors group-hover:bg-primary/5 md:col-span-3">
								<div className="font-semibold text-base text-primary tracking-tight transition-colors group-hover:text-foreground-light">
									{title}
								</div>
								{date && (
									<DateTree date={date} className="mt-1 mb-2 md:hidden" />
								)}
								{excerpt && (
									<p className="mt-1.5 line-clamp-2 text-foreground/60 text-sm transition-colors group-hover:text-foreground/80">
										{excerpt}
									</p>
								)}
								<span className="mt-3 inline-flex translate-x-0 items-center gap-1 font-medium text-primary/60 text-sm opacity-0 transition-transform transition-opacity duration-200 group-hover:translate-x-1 group-hover:opacity-100">
									{m.note_read_card()} <Iconify icon="lucide:chevron-right" />{' '}
								</span>
							</article>
							{date && (
								<p className="sticky top-20 z-10 order-first mt-1 hidden text-secondary text-sm md:block">
									{date}
								</p>
							)}
						</Motion>
					</Link>
				)}
			/>
			{image && (
				<HoverCardContent
					render={<Img width={200} className="max-h-42" src={image} />}
					side="right"
				/>
			)}
		</HoverCard>
	);
}

function NoteGridCard({
	title,
	body,
	description: _description,
	publishedAt,
	slug,
	tags,
	image: _image,
	className,
	date,
	excerpt,
	...props
}: NoteCardContentProps) {
	const href = '/note/$slug' as const;
	const params = { slug: slug?.current ?? '' } as never;
	const parsedTags = parseTags(tags).slice(0, 6);
	const category = parsedTags[0] ?? 'Engineering';

	return (
		<Motion {...props}>
			<Card className={cn('group flex flex-col gap-4 p-5', className)}>
				{/* ── Header: metadata row + category badge ── */}
				<div className="flex items-center justify-between gap-3">
					<div className="flex items-center gap-2 font-mono text-2xs text-muted-foreground uppercase tracking-[0.15em]">
						{date && <time dateTime={publishedAt ?? undefined}>{date}</time>}
						{date && body && (
							<>
								<span aria-hidden="true" className="text-border">
									·
								</span>
								<span>{estimateReadTime(body)}</span>
							</>
						)}
					</div>
					<Chip className="shrink-0">{category}</Chip>
				</div>

				{/* ── Title ── */}
				<h3 className="font-xblk text-foreground-light text-xl leading-tight transition-colors duration-300 group-hover:text-primary">
					{title}
				</h3>

				{/* ── Excerpt ── */}
				{excerpt && (
					<p className="line-clamp-2 font-sans text-foreground/60 text-sm leading-relaxed">
						{excerpt}
					</p>
				)}

				{/* ── Tags ── */}
				{parsedTags.length > 0 && (
					<div className="flex flex-wrap gap-2">
						{parsedTags.map((tag) => (
							<span
								key={tag}
								className="rounded-full bg-muted/60 px-2.5 py-0.5 font-mono text-3xs text-muted-foreground tracking-wider"
							>
								#{tag}
							</span>
						))}
					</div>
				)}

				{/* ── Footer: action + ID ── */}
				<div className="mt-auto flex items-center justify-between border-border border-t pt-3">
					<span className="inline-flex translate-x-0 items-center gap-1.5 font-matrix text-primary text-sm transition-transform duration-300 group-hover:translate-x-1">
						{m.note_read_card()}
						<span
							aria-hidden="true"
							className="inline-block transition-transform duration-300 group-hover:translate-x-0.5"
						>
							→
						</span>
					</span>
					{slug?.current && (
						<span className="font-mono text-2xs text-muted-foreground uppercase tracking-wider">
							ID: {noteIdFromSlug(slug.current)}
						</span>
					)}
				</div>

				{/* ── Full-card link overlay ── */}
				<Link
					to={href}
					params={params}
					className="corner-bevel absolute inset-0 z-10 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
					viewTransition={{ types: ['slide-left'] }}
					aria-label={m.note_read_card_aria({ title: title ?? '' })}
				/>
			</Card>
		</Motion>
	);
}

export function NoteCard({
	body,
	description,
	publishedAt,
	page,
	...props
}: NoteCardProps) {
	const date = publishedAt ? formatDate(publishedAt, { month: 'long' }) : null;
	const excerpt = description
		? description
		: body
			? `${body.slice(0, 160).replace(/\n/g, ' ').trim()}…`
			: null;
	const cardProps = { ...props, body, description, publishedAt, date, excerpt };

	return page ? (
		<NotePageCard {...cardProps} />
	) : (
		<NoteGridCard {...cardProps} />
	);
}
